/**
 * What each layer of the connection dialog does in a transition (Option C, C1b, plan §3.2, the prototype's
 * `flipItems` / `crossfadeViews` without its cloned ghosts). Pure: it turns measured boxes into keyframes and timings;
 * `flipDom.ts` measures and `useSharedLayoutTransition` plays.
 *
 * The DOM contract: an ITEM carries `data-flip-key` (`engine:<id>`, `entry:<id>`, `caption:<group>`, `filter`), unique
 * per view; its LAYERS carry `data-flip-layer`. A wrapper is never animated; its layers are siblings, so text never
 * inherits the surface's size change:
 * - `surface` (background, border, radius) tweens position and SIZE (width / height on an absolute box, as the
 *   prototype's ghosts do, so its border and radius never stretch); its opacity follows the view's own rest opacity
 *   (a rail item's surface shows only on the current engine, so the other cards' surfaces fade out on the way in);
 * - `icon` / `label` translate with a UNIFORM scale (icon width ratio, font-size ratio);
 * - `blurb` / `chevron` (card-only parts) travel with the label and fade: out in 90 ms on the way in (the rail item
 *   carries a hidden "ghost" copy for that), in over the last 45 % on the way back;
 * - `bar` / `dot` (rail-only marks) fade in over the second half.
 * An item whose partner is missing (a card the filter hid, the filter row, a caption only one view has) fades in as a
 * whole; `data-flip-chrome` parts the other view lacks fade in (`fade`) or slide in as the page recipe (`pane`).
 */
import { ORIGIN, offsetBetween, px, translate, translateScale, uniformScale, type Box, type Point } from './flipMath';
import type { AnimationMeta } from './flipController';
import type { MotionTokens } from './motionTokens';

/** The view a navigation goes to. */
export type FlightTarget = 'start' | 'form';

export type LayerState = { box: Box; opacity: number; fontSize: number; fontWeight: number };

export type SceneLayer<T> = { target: T; key: string; name: string; ghost: boolean; state: LayerState };

export type FlipScene<T> = {
  /** The layers before the commit, by `<key>/<name>`. */
  first: ReadonlyMap<string, LayerState>;
  layers: readonly SceneLayer<T>[];
  items: readonly { target: T; key: string }[];
  chrome: readonly { target: T; kind: 'fade' | 'pane' }[];
};

export type AnimationTiming = { duration: number; delay?: number; easing: string; fill?: 'backwards' | 'forwards' };

export type AnimationSpec<T> = {
  target: T;
  keyframes: Keyframe[];
  timing: AnimationTiming;
  meta: AnimationMeta;
  /** Inline style for the flight (a ghost's width), removed by the cleanup. */
  style?: { width: string };
  /** The target leaves the document when its own animation ends (an outgoing copy). */
  removeWhenDone?: boolean;
};

export const layerId = (key: string, name: string) => `${key}/${name}`;

type Kind = 'surface' | 'glyph' | 'text' | 'part' | 'mark';

function kindOf(name: string): Kind {
  if (name === 'surface') return 'surface';
  if (name === 'icon') return 'glyph';
  if (name === 'label') return 'text';
  if (name === 'bar' || name === 'dot') return 'mark';
  return 'part';
}

const differs = (a: number, b: number) => Math.abs(a - b) > 0.01;
const negate = ({ x, y }: Point): Point => ({ x: -x, y: -y });
const plus = (a: Point, b: Point): Point => ({ x: a.x + b.x, y: a.y + b.y });

/**
 * When the incoming view's own parts may start (final UX review UX-06): once the outgoing copy is gone (it fades in
 * `partOut`), so no text is ever drawn over the view being left. Only the shared layers move from the first frame.
 */
export const incomingDelay = (t: MotionTokens) => t.partOut;

/** Incoming chrome and unmatched items, after the outgoing copy has faded. */
function fadeIn<T>(target: T, _to: FlightTarget, t: MotionTokens, meta: AnimationMeta = { props: ['opacity'] }): AnimationSpec<T> {
  return { target, keyframes: [{ opacity: 0 }, { opacity: 1 }], timing: { duration: t.fast, delay: incomingDelay(t), easing: t.ease, fill: 'backwards' }, meta };
}

/** A box or part that grows in with its label (final UX review UX-05): from 30 % of the move, over the rest of it. */
const growIn = (t: MotionTokens): AnimationTiming => ({ duration: t.shared * 0.7, delay: t.shared * 0.3, easing: t.ease, fill: 'backwards' });

/** The page slide (transitions-dev 08): 8 px, a 3 px blur, from the side the page comes from. */
function slideIn<T>(target: T, fromRight: boolean, t: MotionTokens, delay?: number): AnimationSpec<T> {
  const from: Keyframe = { opacity: 0, transform: `translateX(${fromRight ? t.distance : -t.distance}px)` };
  const to: Keyframe = { opacity: 1, transform: 'translateX(0px)' };
  if (t.paneBlur > 0) {
    from.filter = `blur(${t.paneBlur}px)`;
    to.filter = 'blur(0px)';
  }
  return { target, keyframes: [from, to], timing: { duration: t.fast, delay, easing: t.ease, fill: 'backwards' }, meta: { props: ['opacity', 'transform', 'filter'] } };
}

function opacitySpec<T>(layer: SceneLayer<T>, from: number, timing: AnimationTiming): AnimationSpec<T> {
  return { target: layer.target, keyframes: [{ opacity: from }, { opacity: layer.state.opacity }], timing, meta: { key: layer.key, layer: layer.name, props: ['opacity'] } };
}

function layerSpecs<T>(layer: SceneLayer<T>, first: LayerState | undefined, labelShift: Point | undefined, t: MotionTokens): AnimationSpec<T>[] {
  const last = layer.state;
  const meta = (props: string[]): AnimationMeta => ({ key: layer.key, layer: layer.name, props });
  const move: AnimationTiming = { duration: t.shared, easing: t.ease };
  const kind = kindOf(layer.name);

  // An item only some of whose layers have a partner (the detection card's engine icon, flying into its rail item on
  // Continue): the partner-less box and label fade in with the view instead of popping in at rest.
  if (!first && (kind === 'surface' || kind === 'text' || kind === 'glyph')) {
    return last.opacity > 0 ? [opacitySpec(layer, 0, { duration: t.fast, delay: incomingDelay(t), easing: t.ease, fill: 'backwards' })] : [];
  }
  if (kind === 'mark' || !first) {
    if (kind === 'part' && !layer.ghost) return partIn(layer, first, labelShift, t);
    if (kind === 'mark' && differs(first?.opacity ?? 0, last.opacity)) {
      return [opacitySpec(layer, first?.opacity ?? 0, { duration: t.shared / 2, delay: t.shared / 2, easing: t.ease, fill: 'backwards' })];
    }
    return [];
  }

  const offset = offsetBetween(first.box, last.box);
  const specs: AnimationSpec<T>[] = [];
  if (kind === 'surface') {
    specs.push({
      target: layer.target,
      keyframes: [
        { transform: translate(offset), width: px(first.box.w), height: px(first.box.h) },
        { transform: translate(ORIGIN), width: px(last.box.w), height: px(last.box.h) },
      ],
      timing: move,
      meta: meta(['transform', 'width', 'height']),
    });
  } else if (kind === 'glyph' || kind === 'text') {
    const scale = kind === 'glyph' ? uniformScale(first.box.w, last.box.w) : uniformScale(first.fontSize, last.fontSize);
    const from: Keyframe = { transform: translateScale(offset, scale) };
    const to: Keyframe = { transform: translateScale(ORIGIN, 1) };
    // A card's medium label becomes a rail item's regular one (and back) over the move, as the prototype's name does.
    const weighted = kind === 'text' && first.fontWeight !== last.fontWeight;
    if (weighted) {
      from.fontWeight = String(first.fontWeight);
      to.fontWeight = String(last.fontWeight);
    }
    specs.push({ target: layer.target, keyframes: [from, to], timing: move, meta: meta(weighted ? ['transform', 'font-weight'] : ['transform']) });
  } else if (layer.ghost) {
    // A rail item's hidden copy of the card's blurb: it starts where the card's blurb is, travels with the label and is
    // gone after 90 ms, wrapped at the card's width.
    const end = plus(offset, labelShift ?? ORIGIN);
    specs.push({ target: layer.target, keyframes: [{ transform: translate(offset) }, { transform: translate(end) }], timing: move, meta: meta(['transform']), style: { width: px(first.box.w) } });
    // A ghost is never visible at rest: it always ends transparent.
    specs.push({ target: layer.target, keyframes: [{ opacity: first.opacity }, { opacity: 0 }], timing: { duration: t.partOut, easing: t.ease }, meta: meta(['opacity']) });
    return specs;
  } else {
    return partIn(layer, first, labelShift, t);
  }

  if (differs(first.opacity, last.opacity)) {
    // A box that is not the chosen one leaves like a card-only part (gone in 90 ms, review C1B-03: the moving boxes never
    // pile up) and grows back in with its label from 30 % of the move (UX-05: the last frames used to carry all the chrome).
    const fadingOut = last.opacity < first.opacity;
    specs.push(opacitySpec(layer, first.opacity, fadingOut ? { duration: t.partOut, easing: t.ease } : growIn(t)));
  }
  return specs;
}

/** A card-only part on the way back: it travels with its label and grows in from 30 % of the move (UX-05). */
function partIn<T>(layer: SceneLayer<T>, first: LayerState | undefined, labelShift: Point | undefined, t: MotionTokens): AnimationSpec<T>[] {
  const specs: AnimationSpec<T>[] = [];
  const from = first ? offsetBetween(first.box, layer.state.box) : labelShift ? negate(labelShift) : null;
  if (from) {
    specs.push({ target: layer.target, keyframes: [{ transform: translate(from) }, { transform: translate(ORIGIN) }], timing: { duration: t.shared, easing: t.ease }, meta: { key: layer.key, layer: layer.name, props: ['transform'] } });
  }
  const start = first?.opacity ?? 0;
  if (differs(start, layer.state.opacity)) specs.push(opacitySpec(layer, start, growIn(t)));
  return specs;
}

/** The shared-element FLIP between the start view and the two-pane view. */
export function planSharedFlip<T>(scene: FlipScene<T>, to: FlightTarget, t: MotionTokens): AnimationSpec<T>[] {
  const firstOf = (layer: SceneLayer<T>) => scene.first.get(layerId(layer.key, layer.name));
  const matched = new Set(scene.layers.filter((layer) => firstOf(layer)).map((layer) => layer.key));
  const labelShift = new Map<string, Point>();
  for (const layer of scene.layers) {
    const first = firstOf(layer);
    if (layer.name === 'label' && first) labelShift.set(layer.key, offsetBetween(layer.state.box, first.box));
  }

  const specs: AnimationSpec<T>[] = [];
  for (const layer of scene.layers) {
    if (matched.has(layer.key)) specs.push(...layerSpecs(layer, firstOf(layer), labelShift.get(layer.key), t));
  }
  for (const item of scene.items) {
    if (!matched.has(item.key)) specs.push(fadeIn(item.target, to, t, { key: item.key, props: ['opacity'] }));
  }
  for (const part of scene.chrome) {
    specs.push(part.kind === 'pane' ? slideIn(part.target, true, t, incomingDelay(t)) : fadeIn(part.target, to, t));
  }
  return specs;
}

/**
 * The view being left (its outgoing copy, `flipOutgoing.ts`) fades out from the opacity it had, so no frame is empty
 * (review C1B-03). With full motion it goes in 90 ms and the incoming parts start after it (`incomingDelay`): a fade
 * through, never text over text (final UX review UX-06). Reduced motion keeps the 150 ms cross-fade. Opacity only.
 */
export function planOutgoingFade<T>(copies: readonly { target: T; opacity: number }[], mode: 'full' | 'reduced', t: MotionTokens): AnimationSpec<T>[] {
  return copies.map(({ target, opacity }) => ({
    target,
    keyframes: [{ opacity }, { opacity: 0 }],
    timing: mode === 'reduced' ? { duration: t.quick, easing: t.swapEase, fill: 'forwards' } : { duration: t.partOut, easing: t.ease, fill: 'forwards' },
    meta: { props: ['opacity'] },
    removeWhenDone: true,
  }));
}

/**
 * A view switch without the FLIP: reduced motion cross-fades the incoming page (150 ms, no movement); a narrow window
 * slides it in from its side (transitions-dev 08: the form comes from the right, the start view from the left).
 */
export function planPageSwap<T>(pages: readonly T[], to: FlightTarget, mode: 'full' | 'reduced', t: MotionTokens): AnimationSpec<T>[] {
  if (mode === 'reduced') {
    return pages.map((target) => ({ target, keyframes: [{ opacity: 0 }, { opacity: 1 }], timing: { duration: t.quick, easing: t.swapEase }, meta: { props: ['opacity'] } }));
  }
  return pages.map((target) => slideIn(target, to === 'form', t, incomingDelay(t)));
}

export type RailSwitchScene<T> = {
  pane: T | null;
  /** The chosen item's surface and where the previous one was (two panes, full motion, no flight running). */
  highlight: { target: T; key: string; from: Box; to: Box } | null;
};

/**
 * An engine switch inside the form view (rail, compact select, a paste or Azure pick for another engine): no FLIP. The
 * pane swaps in (a cross-blur when the pane blur is on); the rail highlight slides from the previous item (tabs
 * sliding). Reduced motion: the pane cross-fades, nothing moves.
 */
export function planRailSwitch<T>(scene: RailSwitchScene<T>, mode: 'full' | 'reduced', t: MotionTokens): AnimationSpec<T>[] {
  const specs: AnimationSpec<T>[] = [];
  if (scene.pane) {
    const blurred = mode === 'full' && t.swapBlur > 0;
    const keyframes: Keyframe[] = blurred ? [{ opacity: 0, filter: `blur(${t.swapBlur}px)` }, { opacity: 1, filter: 'blur(0px)' }] : [{ opacity: 0 }, { opacity: 1 }];
    // No fade-through delay here (UX review r2, R-1): the old pane's copy is gone in 90 ms (under 10 % by ~33 ms) while
    // the swap curve starts slow, so the two never both show — and a delay only left the pane blank for ~90 ms.
    const timing: AnimationTiming = { duration: mode === 'reduced' ? t.quick : t.fast, easing: t.swapEase };
    specs.push({ target: scene.pane, keyframes, timing, meta: { props: blurred ? ['opacity', 'filter'] : ['opacity'] } });
  }
  const highlight = mode === 'full' ? scene.highlight : null;
  if (highlight) {
    specs.push({
      target: highlight.target,
      keyframes: [
        { transform: translate(offsetBetween(highlight.from, highlight.to)), width: px(highlight.from.w), height: px(highlight.from.h) },
        { transform: translate(ORIGIN), width: px(highlight.to.w), height: px(highlight.to.h) },
      ],
      timing: { duration: t.fast, easing: t.ease },
      meta: { key: highlight.key, layer: 'surface', props: ['transform', 'width', 'height'] },
    });
  }
  return specs;
}
